import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, Wallet, QrCode, ArrowLeft, RefreshCw, 
  ShoppingCart, Plus, Minus, Trash2, CheckCircle, XCircle,
  AlertTriangle, LogOut, Settings, Monitor, X, Users,
  LayoutGrid, Package, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { formatCurrency } from '@/lib/utils';
import { CieloPixDialog, useCieloStatus } from '@/components/cielo/CieloPixDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Estados de navegação
const POS_SCREENS = {
  TERMINAL_SELECT: 'terminal',
  HOME: 'home',
  TABLES: 'tables',
  ORDER: 'order',
  PRODUCTS: 'products',
  PAYMENT: 'payment',
  CASH: 'cash'
};

// Estados de pagamento
const PAYMENT_STATE = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  DECLINED: 'declined',
  ERROR: 'error',
  TIMEOUT: 'timeout',
  CANCELLED: 'cancelled'
};

export function POSWebPage() {
  const navigate = useNavigate();
  
  // Estado global
  const [currentScreen, setCurrentScreen] = useState(POS_SCREENS.TERMINAL_SELECT);
  const [terminal, setTerminal] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [cashOpen, setCashOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dados
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [selectedTable, setSelectedTable] = useState(null);
  
  // Modal de abertura de mesa
  const [showOpenTableModal, setShowOpenTableModal] = useState(false);
  const [tableToOpen, setTableToOpen] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [peopleCount, setPeopleCount] = useState(1);
  
  // Pagamento
  const [paymentState, setPaymentState] = useState(PAYMENT_STATE.IDLE);
  const [selectedPaymentType, setSelectedPaymentType] = useState('CREDIT');
  const cielo = useCieloStatus();
  const [cieloOpen, setCieloOpen] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  
  // Estatísticas
  const [stats, setStats] = useState({
    tablesOccupied: 0,
    tablesTotal: 0,
    pendingOrders: 0
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      const [terminalsRes, cashRes, tablesRes] = await Promise.all([
        api.get(`${API_URL}/api/pos/terminals`).catch(() => ({ data: [] })),
        api.get(`${API_URL}/api/cash/session/current`).catch(() => ({ data: { has_open_session: false } })),
        api.get(`${API_URL}/api/pdv/tables`).catch(() => ({ data: [] }))
      ]);
      
      setTerminals(terminalsRes.data || []);
      setCashOpen(cashRes.data?.has_open_session || false);
      setTables(tablesRes.data || []);
      
      const occupied = (tablesRes.data || []).filter(t => t.status === 'OCUPADA').length;
      setStats({
        tablesOccupied: occupied,
        tablesTotal: (tablesRes.data || []).length,
        pendingOrders: 0
      });
      
      const savedTerminal = localStorage.getItem('pos_terminal');
      if (savedTerminal) {
        try {
          const t = JSON.parse(savedTerminal);
          setTerminal(t);
          setCurrentScreen(POS_SCREENS.HOME);
        } catch (e) {
          localStorage.removeItem('pos_terminal');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados do POS');
    } finally {
      setIsLoading(false);
    }
  };

  const selectTerminal = (t) => {
    setTerminal(t);
    localStorage.setItem('pos_terminal', JSON.stringify(t));
    setCurrentScreen(POS_SCREENS.HOME);
    toast.success(`Terminal ${t.terminal_code} selecionado`);
  };

  const handleLogout = () => {
    localStorage.removeItem('pos_terminal');
    setTerminal(null);
    setCurrentScreen(POS_SCREENS.TERMINAL_SELECT);
  };

  // === HANDLERS DE MESA ===
  const loadTables = async () => {
    try {
      const res = await api.get(`${API_URL}/api/pdv/tables`);
      setTables(res.data || []);
      const occupied = (res.data || []).filter(t => t.status === 'OCUPADA').length;
      setStats(s => ({ ...s, tablesOccupied: occupied, tablesTotal: (res.data || []).length }));
    } catch (error) {
      toast.error('Erro ao carregar mesas');
    }
  };

  const handleTableClick = (table) => {
    if (table.status === 'OCUPADA' && table.current_order?.id) {
      // Mesa ocupada com pedido - ir direto para o pedido
      setSelectedTable(table);
      loadOrder(table.current_order.id);
      setCurrentScreen(POS_SCREENS.ORDER);
    } else if (table.status === 'LIVRE' || (table.status === 'OCUPADA' && !table.current_order?.id)) {
      // Mesa livre OU ocupada sem pedido - abrir modal
      setTableToOpen(table);
      setCustomerName('');
      setPeopleCount(1);
      setShowOpenTableModal(true);
    } else {
      toast.info('Mesa em limpeza');
    }
  };

  const handleOpenTable = async () => {
    if (!tableToOpen) return;
    try {
      const res = await api.post(`${API_URL}/api/pdv/tables/${tableToOpen.id}/open`, {
        table_id: tableToOpen.id,
        customer_name: customerName || null,
        people_count: peopleCount
      });
      
      setShowOpenTableModal(false);
      setSelectedTable(tableToOpen);
      
      if (res.data?.order_id) {
        await loadOrder(res.data.order_id);
      }
      setCurrentScreen(POS_SCREENS.ORDER);
      toast.success('Mesa aberta!');
    } catch (error) {
      console.error('Erro ao abrir mesa:', error);
      toast.error(error.response?.data?.detail || 'Erro ao abrir mesa');
    }
  };

  // === HANDLERS DE PEDIDO ===
  const loadOrder = async (orderId) => {
    if (!orderId) return;
    try {
      const res = await api.get(`${API_URL}/api/orders/${orderId}`);
      setCurrentOrder(res.data);
    } catch (error) {
      console.error('Erro ao carregar pedido:', error);
      toast.error('Erro ao carregar pedido');
    }
  };

  const loadCategories = async () => {
    try {
      const res = await api.get(`${API_URL}/api/categories`);
      setCategories(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    }
  };

  const loadProducts = async (categoryId = null) => {
    try {
      const url = categoryId 
        ? `${API_URL}/api/products?category_id=${categoryId}`
        : `${API_URL}/api/products`;
      const res = await api.get(url);
      setProducts(res.data || []);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    }
  };

  const handleAddProduct = async (product, quantity = 1) => {
    if (!currentOrder?.id) {
      toast.error('Nenhum pedido selecionado');
      return;
    }
    try {
      const res = await api.post(`${API_URL}/api/pdv/orders/${currentOrder.id}/items`, {
        product_id: product.id,
        quantity: quantity,
        notes: null,
        modifiers: null
      });
      await loadOrder(currentOrder.id);
      toast.success(`${product.name} adicionado`);
      // Voltar para tela de pedido
      setCurrentScreen(POS_SCREENS.ORDER);
    } catch (error) {
      console.error('Erro ao adicionar produto:', error);
      toast.error(error.response?.data?.detail || 'Erro ao adicionar produto');
    }
  };

  const handleRemoveItem = async (itemId) => {
    if (!currentOrder?.id) return;
    try {
      const res = await api.delete(`${API_URL}/api/pdv/orders/${currentOrder.id}/items/${itemId}`);
      await loadOrder(currentOrder.id);
      toast.success('Item removido');
    } catch (error) {
      toast.error('Erro ao remover item');
    }
  };

  // === HANDLERS DE PAGAMENTO ===
  const handleProcessPayment = async () => {
    if (!currentOrder?.id) return;
    
    setPaymentState(PAYMENT_STATE.PROCESSING);
    
    try {
      const idempotencyKey = crypto.randomUUID();
      
      const startRes = await api.post(`${API_URL}/api/pos/payment/start`, {
        order_id: currentOrder.id,
        amount: currentOrder.total,
        payment_method_id: selectedPaymentType === 'PIX' ? 'pix' : 'card',
        idempotency_key: idempotencyKey,
        terminal_id: terminal?.id || 'WEB'
      });
      
      const transactionId = startRes.data?.transaction_id;
      
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const confirmRes = await api.post(`${API_URL}/api/pos/payment/${transactionId}/confirm`, {
        nsu: Math.floor(100000 + Math.random() * 900000).toString(),
        authorization_code: Math.floor(100000 + Math.random() * 900000).toString(),
        brand: selectedPaymentType === 'PIX' ? 'PIX' : ['VISA', 'MASTERCARD', 'ELO'][Math.floor(Math.random() * 3)],
        card_last_digits: selectedPaymentType === 'PIX' ? null : '****',
        acquirer: 'SIMULADOR_WEB'
      });
      
      setPaymentResult({
        nsu: confirmRes.data?.nsu || startRes.data?.nsu,
        authorizationCode: confirmRes.data?.authorization_code,
        cardBrand: confirmRes.data?.brand,
        cardLastDigits: confirmRes.data?.card_last_digits
      });
      setPaymentState(PAYMENT_STATE.SUCCESS);
      toast.success('Pagamento aprovado!');
      
    } catch (error) {
      console.error('Erro no pagamento:', error);
      setPaymentState(PAYMENT_STATE.ERROR);
      setPaymentResult({ errorMessage: error.response?.data?.detail || 'Erro ao processar pagamento' });
    }
  };

  // === HANDLERS DE CAIXA ===
  const handleOpenCash = async (initialAmount) => {
    try {
      await api.post(`${API_URL}/api/cash/session/open`, {
        opening_amount: initialAmount,
        notes: 'Abertura via POS Web'
      });
      setCashOpen(true);
      toast.success('Caixa aberto com sucesso');
    } catch (error) {
      toast.error('Erro ao abrir caixa');
    }
  };

  const handleCloseCash = async (declaredAmount) => {
    try {
      await api.post(`${API_URL}/api/cash/session/close`, {
        closing_amount: declaredAmount,
        notes: 'Fechamento via POS Web'
      });
      setCashOpen(false);
      toast.success('Caixa fechado com sucesso');
    } catch (error) {
      toast.error('Erro ao fechar caixa');
    }
  };

  // === RENDER ===
  const renderScreen = () => {
    switch (currentScreen) {
      case POS_SCREENS.TERMINAL_SELECT:
        return <TerminalSelectScreen terminals={terminals} onSelect={selectTerminal} isLoading={isLoading} />;
      case POS_SCREENS.HOME:
        return <HomeScreen terminal={terminal} cashOpen={cashOpen} stats={stats} onNavigate={setCurrentScreen} onLogout={handleLogout} onRefresh={loadInitialData} />;
      case POS_SCREENS.TABLES:
        return <TablesScreen tables={tables} onBack={() => setCurrentScreen(POS_SCREENS.HOME)} onSelectTable={handleTableClick} onRefresh={loadTables} />;
      case POS_SCREENS.ORDER:
        return <OrderScreen order={currentOrder} table={selectedTable} onBack={() => { setCurrentScreen(POS_SCREENS.TABLES); loadTables(); }} onAddProducts={() => { loadCategories(); loadProducts(); setCurrentScreen(POS_SCREENS.PRODUCTS); }} onPayment={() => setCurrentScreen(POS_SCREENS.PAYMENT)} onRefresh={() => loadOrder(currentOrder?.id)} onRemoveItem={handleRemoveItem} />;
      case POS_SCREENS.PRODUCTS:
        return <ProductsScreen categories={categories} products={products} onBack={() => setCurrentScreen(POS_SCREENS.ORDER)} onAddProduct={handleAddProduct} onLoadProducts={loadProducts} />;
      case POS_SCREENS.PAYMENT:
        return <><PaymentScreen order={currentOrder} paymentState={paymentState} paymentResult={paymentResult} selectedPaymentType={selectedPaymentType} onSelectType={setSelectedPaymentType} onProcess={handleProcessPayment} onBack={() => { setPaymentState(PAYMENT_STATE.IDLE); setCurrentScreen(POS_SCREENS.ORDER); }} onComplete={() => { setPaymentState(PAYMENT_STATE.IDLE); setCurrentOrder(null); setSelectedTable(null); setCurrentScreen(POS_SCREENS.HOME); loadTables(); }} onRetry={() => { setPaymentState(PAYMENT_STATE.IDLE); setPaymentResult(null); }} />
          {cielo.pix_enabled && paymentState === PAYMENT_STATE.IDLE && currentOrder?.id && <button data-testid="pos-cielo-pix-btn" onClick={() => setCieloOpen(true)} className="fixed bottom-4 right-4 z-40 px-5 py-3 rounded-full bg-emerald-600 text-white font-semibold shadow-lg">PIX Cielo (QR Code)</button>}
          <CieloPixDialog open={cieloOpen} orderId={currentOrder?.id} onPaid={(c) => { setPaymentResult({ nsu: c.nsu || c.sent_order_id, authorizationCode: c.authorization_code, cardBrand: 'PIX Cielo', cardLastDigits: null }); setPaymentState(PAYMENT_STATE.SUCCESS); setTimeout(() => setCieloOpen(false), 1500); }} onClose={() => setCieloOpen(false)} />
        </>;
      case POS_SCREENS.CASH:
        return <CashScreen cashOpen={cashOpen} onBack={() => setCurrentScreen(POS_SCREENS.HOME)} onOpenCash={handleOpenCash} onCloseCash={handleCloseCash} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1A1A2E] to-[#16213E]">
      {renderScreen()}
      
      {/* Modal Abrir Mesa */}
      {showOpenTableModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#E94560]/10 flex items-center justify-center">
                <LayoutGrid className="w-5 h-5 text-[#E94560]" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Abrir {tableToOpen?.name || 'Mesa'}</h2>
              <button onClick={() => setShowOpenTableModal(false)} className="ml-auto p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Cliente (opcional)</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Nome do cliente"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#E94560] focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Número de Pessoas</label>
                <div className="flex items-center gap-4">
                  <button 
                    onClick={() => setPeopleCount(Math.max(1, peopleCount - 1))}
                    className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <span className="text-2xl font-bold w-12 text-center">{peopleCount}</span>
                  <button 
                    onClick={() => setPeopleCount(peopleCount + 1)}
                    className="w-12 h-12 rounded-xl border border-gray-200 flex items-center justify-center hover:bg-gray-50"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowOpenTableModal(false)}
                className="flex-1 py-3 border border-gray-200 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleOpenTable}
                className="flex-1 py-3 bg-[#E94560] text-white rounded-xl font-medium hover:bg-[#E94560]/90"
              >
                Abrir Mesa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TELA: Seleção de Terminal
// ============================================================
function TerminalSelectScreen({ terminals, onSelect, isLoading }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-[#E94560] mb-2">HIT GRILL</h1>
        <p className="text-white/70">POS Web - Selecione o Terminal</p>
      </div>
      
      {isLoading ? (
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E94560]"></div>
      ) : terminals.length === 0 ? (
        <div className="text-center">
          <Monitor className="w-16 h-16 text-white/40 mx-auto mb-4" />
          <p className="text-white/60">Nenhum terminal cadastrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl">
          {terminals.map((t) => (
            <button key={t.id} onClick={() => onSelect(t)} className="bg-white/10 hover:bg-white/20 rounded-xl p-6 text-left transition-all border border-white/10 hover:border-[#E94560]">
              <Monitor className="w-10 h-10 text-[#E94560] mb-3" />
              <h3 className="text-white font-bold text-lg">{t.terminal_code}</h3>
              <p className="text-white/60 text-sm">{t.terminal_name || 'Terminal POS'}</p>
              <div className="mt-3 flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${t.status === 'ACTIVE' ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className="text-white/50 text-xs">{t.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TELA: Home
// ============================================================
function HomeScreen({ terminal, cashOpen, stats, onNavigate, onLogout, onRefresh }) {
  const menuItems = [
    { icon: Wallet, title: 'Caixa', subtitle: cashOpen ? 'Aberto' : 'Fechado', color: '#4CAF50', screen: POS_SCREENS.CASH },
    { icon: LayoutGrid, title: 'Mesas', subtitle: `${stats.tablesOccupied}/${stats.tablesTotal} ocupadas`, color: '#2196F3', screen: POS_SCREENS.TABLES },
    { icon: ShoppingCart, title: 'Venda Rápida', subtitle: 'Sem mesa', color: '#E94560', screen: null },
    { icon: Package, title: 'Produtos', subtitle: 'Catálogo', color: '#9C27B0', screen: POS_SCREENS.PRODUCTS },
    { icon: Receipt, title: 'Pedidos', subtitle: `${stats.pendingOrders} pendentes`, color: '#FF9800', screen: null },
    { icon: Settings, title: 'Config', subtitle: 'Configurações', color: '#607D8B', screen: null },
  ];

  return (
    <div className="min-h-screen p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#E94560]">HIT GRILL</h1>
          <p className="text-white/70 text-sm">Terminal: {terminal?.terminal_code}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${cashOpen ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
            {cashOpen ? 'Caixa Aberto' : 'Caixa Fechado'}
          </div>
          <button onClick={onRefresh} className="p-2 text-white/60 hover:text-white"><RefreshCw className="w-5 h-5" /></button>
          <button onClick={onLogout} className="p-2 text-white/60 hover:text-white"><LogOut className="w-5 h-5" /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
        {menuItems.map((item, idx) => (
          <button key={idx} onClick={() => item.screen && onNavigate(item.screen)} disabled={!item.screen} className="bg-white/10 hover:bg-white/15 disabled:opacity-50 disabled:cursor-not-allowed rounded-2xl p-6 aspect-square flex flex-col items-center justify-center transition-all">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${item.color}33` }}>
              <item.icon className="w-8 h-8" style={{ color: item.color }} />
            </div>
            <h3 className="text-white font-bold text-lg">{item.title}</h3>
            <p className="text-white/50 text-sm">{item.subtitle}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// TELA: Mesas
// ============================================================
function TablesScreen({ tables, onBack, onSelectTable, onRefresh }) {
  const getTableColor = (status) => {
    switch (status) {
      case 'LIVRE': return '#4CAF50';
      case 'OCUPADA': return '#E94560';
      case 'LIMPEZA': return '#FFB74D';
      default: return '#607D8B';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'LIVRE': return 'Livre';
      case 'OCUPADA': return 'Ocupada';
      case 'LIMPEZA': return 'Limpeza';
      default: return status;
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 text-white hover:bg-white/10 rounded-lg"><ArrowLeft className="w-6 h-6" /></button>
        <h1 className="text-2xl font-bold text-white flex-1">Mesas</h1>
        <button onClick={onRefresh} className="p-2 text-white/60 hover:text-white"><RefreshCw className="w-5 h-5" /></button>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {tables.map((table) => {
          const color = getTableColor(table.status);
          return (
            <button key={table.id} onClick={() => onSelectTable(table)} className="aspect-square rounded-xl p-4 flex flex-col items-center justify-center transition-all hover:scale-105" style={{ backgroundColor: `${color}33` }}>
              <LayoutGrid className="w-8 h-8 mb-2" style={{ color }} />
              <span className="text-white font-bold">{table.name || table.number}</span>
              <span className="text-xs" style={{ color }}>{getStatusText(table.status)}</span>
              {table.total > 0 && <span className="text-white text-sm font-medium mt-1">{formatCurrency(table.total)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// TELA: Pedido
// ============================================================
function OrderScreen({ order, table, onBack, onAddProducts, onPayment, onRefresh, onRemoveItem }) {
  const items = order?.items || [];
  const total = order?.total || 0;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bg-[#17120F] p-4 flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-white hover:bg-white/10 rounded-lg"><ArrowLeft className="w-6 h-6" /></button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">Pedido</h1>
          <p className="text-white/60 text-sm">Mesa: {table?.name || table?.number}</p>
        </div>
        <button onClick={onRefresh} className="p-2 text-white/60 hover:text-white"><RefreshCw className="w-5 h-5" /></button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {items.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/40">Nenhum item no pedido</p>
            <button onClick={onAddProducts} className="mt-4 px-6 py-2 bg-[#FF6B35] text-white rounded-lg hover:bg-[#FF6B35]/80">Adicionar Produtos</button>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <div key={item.id} className="bg-white/10 rounded-xl p-4 flex items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-white font-medium">{item.product_name || item.name}</h3>
                  <p className="text-white/60 text-sm">{item.quantity}x {formatCurrency(item.unit_price)}</p>
                </div>
                <span className="text-white font-bold">{formatCurrency(item.quantity * item.unit_price)}</span>
                <button onClick={() => onRemoveItem(item.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg"><Trash2 className="w-5 h-5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#17120F] p-4 space-y-4">
        <div className="flex justify-between items-center text-white">
          <span className="text-white/70">Total:</span>
          <span className="text-2xl font-bold">{formatCurrency(total)}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={onAddProducts} className="py-3 bg-white/10 text-white rounded-xl font-medium hover:bg-white/20"><Plus className="w-5 h-5 inline mr-2" />Adicionar</button>
          <button onClick={onPayment} disabled={items.length === 0} className="py-3 bg-[#2E7D32] text-white rounded-xl font-medium hover:bg-[#2E7D32]/80 disabled:opacity-50"><CreditCard className="w-5 h-5 inline mr-2" />Pagamento</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TELA: Produtos
// ============================================================
function ProductsScreen({ categories, products, onBack, onAddProduct, onLoadProducts }) {
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleCategorySelect = async (catId) => {
    setSelectedCategory(catId);
    await onLoadProducts(catId);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="bg-[#17120F] p-4 flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-white hover:bg-white/10 rounded-lg"><ArrowLeft className="w-6 h-6" /></button>
        <h1 className="text-xl font-bold text-white flex-1">Produtos</h1>
      </div>

      <div className="p-4 bg-[#1A1A2E] border-b border-white/10">
        <div className="flex gap-2 overflow-x-auto pb-2">
          <button onClick={() => { setSelectedCategory(null); onLoadProducts(); }} className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${!selectedCategory ? 'bg-[#FF6B35] text-white' : 'bg-white/10 text-white/70'}`}>Todos</button>
          {categories.map((cat) => (
            <button key={cat.id} onClick={() => handleCategorySelect(cat.id)} className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${selectedCategory === cat.id ? 'bg-[#FF6B35] text-white' : 'bg-white/10 text-white/70'}`}>{cat.name}</button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {products.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/40">Nenhum produto encontrado</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {products.filter(p => p.active !== false).map((product) => (
              <button key={product.id} onClick={() => onAddProduct(product)} className="bg-white/10 hover:bg-white/15 rounded-xl p-4 text-left transition-all">
                {product.image_url && <img src={product.image_url} alt={product.name} className="w-full h-24 object-cover rounded-lg mb-3" />}
                <h3 className="text-white font-medium truncate">{product.name}</h3>
                <p className="text-[#FF6B35] font-bold mt-1">{formatCurrency(product.price)}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// TELA: Pagamento
// ============================================================
function PaymentScreen({ order, paymentState, paymentResult, selectedPaymentType, onSelectType, onProcess, onBack, onComplete, onRetry }) {
  const total = order?.total || 0;

  if (paymentState === PAYMENT_STATE.PROCESSING) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gradient-to-b from-[#1A1A2E] to-[#16213E]">
        <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-[#FF6B35] mb-8"></div>
        <h2 className="text-2xl font-bold text-white mb-2">Processando Pagamento</h2>
        <p className="text-white/60">Aguarde a confirmação...</p>
      </div>
    );
  }

  if (paymentState === PAYMENT_STATE.SUCCESS) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gradient-to-b from-[#1A1A2E] to-[#16213E]">
        <CheckCircle className="w-24 h-24 text-[#2E7D32] mb-6" />
        <h2 className="text-3xl font-bold text-[#2E7D32] mb-2">Pagamento Aprovado!</h2>
        <p className="text-2xl font-bold text-white mb-6">{formatCurrency(total)}</p>
        <div className="bg-white/10 rounded-xl p-4 w-full max-w-sm mb-8">
          {paymentResult?.nsu && <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/60">NSU</span><span className="text-white font-medium">{paymentResult.nsu}</span></div>}
          {paymentResult?.authorizationCode && <div className="flex justify-between py-2 border-b border-white/10"><span className="text-white/60">Autorização</span><span className="text-white font-medium">{paymentResult.authorizationCode}</span></div>}
          {paymentResult?.cardBrand && <div className="flex justify-between py-2"><span className="text-white/60">Cartão</span><span className="text-white font-medium">{paymentResult.cardBrand}</span></div>}
        </div>
        <button onClick={onComplete} className="w-full max-w-sm py-4 bg-[#2E7D32] text-white rounded-xl font-bold text-lg">Concluir</button>
      </div>
    );
  }

  if (paymentState === PAYMENT_STATE.ERROR || paymentState === PAYMENT_STATE.DECLINED) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-gradient-to-b from-[#1A1A2E] to-[#16213E]">
        <XCircle className="w-24 h-24 text-[#D32F2F] mb-6" />
        <h2 className="text-3xl font-bold text-[#D32F2F] mb-2">Erro no Pagamento</h2>
        <p className="text-white/60 text-center mb-8">{paymentResult?.errorMessage || 'Ocorreu um erro'}</p>
        <button onClick={onRetry} className="w-full max-w-sm py-4 bg-[#FF6B35] text-white rounded-xl font-bold text-lg mb-3">Tentar Novamente</button>
        <button onClick={onBack} className="w-full max-w-sm py-4 bg-white/10 text-white rounded-xl font-medium">Voltar</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#1A1A2E] to-[#16213E]">
      <div className="bg-[#17120F] p-4 flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-white hover:bg-white/10 rounded-lg"><ArrowLeft className="w-6 h-6" /></button>
        <h1 className="text-xl font-bold text-white">Pagamento</h1>
      </div>

      <div className="flex-1 p-6">
        <div className="bg-[#17120F] rounded-2xl p-6 text-center mb-8">
          <p className="text-white/60 mb-2">Total a Pagar</p>
          <p className="text-4xl font-bold text-white">{formatCurrency(total)}</p>
        </div>

        <h3 className="text-white font-bold mb-4">Forma de Pagamento</h3>
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[{ type: 'CREDIT', icon: CreditCard, label: 'Crédito' }, { type: 'DEBIT', icon: Wallet, label: 'Débito' }, { type: 'PIX', icon: QrCode, label: 'PIX' }].map(({ type, icon: Icon, label }) => (
            <button key={type} onClick={() => onSelectType(type)} className={`p-6 rounded-xl flex flex-col items-center gap-3 transition-all ${selectedPaymentType === type ? 'bg-[#FF6B35] text-white' : 'bg-white/10 text-white hover:bg-white/15'}`}>
              <Icon className="w-8 h-8" />
              <span className="font-medium">{label}</span>
            </button>
          ))}
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-500 font-medium">Modo Simulação</p>
              <p className="text-yellow-500/70 text-sm">Esta versão web simula o TEF.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 bg-[#17120F]">
        <button onClick={onProcess} className="w-full py-4 bg-[#2E7D32] text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2">
          <CreditCard className="w-6 h-6" />Processar Pagamento
        </button>
      </div>
    </div>
  );
}

// ============================================================
// TELA: Caixa
// ============================================================
function CashScreen({ cashOpen, onBack, onOpenCash, onCloseCash }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAction = async () => {
    const value = parseFloat(amount) || 0;
    setLoading(true);
    if (cashOpen) {
      await onCloseCash(value);
    } else {
      await onOpenCash(value);
    }
    setLoading(false);
    setAmount('');
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#1A1A2E] to-[#16213E]">
      <div className="bg-[#17120F] p-4 flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-white hover:bg-white/10 rounded-lg"><ArrowLeft className="w-6 h-6" /></button>
        <h1 className="text-xl font-bold text-white">Caixa</h1>
      </div>

      <div className="flex-1 p-6">
        <div className={`rounded-2xl p-6 text-center mb-8 ${cashOpen ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
          <Wallet className={`w-16 h-16 mx-auto mb-4 ${cashOpen ? 'text-green-400' : 'text-red-400'}`} />
          <h2 className={`text-2xl font-bold ${cashOpen ? 'text-green-400' : 'text-red-400'}`}>{cashOpen ? 'Caixa Aberto' : 'Caixa Fechado'}</h2>
        </div>

        <div className="mb-6">
          <label className="text-white/70 text-sm mb-2 block">{cashOpen ? 'Valor Declarado' : 'Valor de Abertura'}</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" className="w-full p-4 bg-white/10 border border-white/20 rounded-xl text-white text-2xl text-center" />
        </div>

        <button onClick={handleAction} disabled={loading} className={`w-full py-4 text-white rounded-xl font-bold text-lg disabled:opacity-50 ${cashOpen ? 'bg-red-500' : 'bg-green-500'}`}>
          {loading ? 'Processando...' : cashOpen ? 'Fechar Caixa' : 'Abrir Caixa'}
        </button>
      </div>
    </div>
  );
}

export default POSWebPage;
